import { Menu, X, ChevronDown, Globe, Smartphone, Database, Shield, Code, Settings, ArrowRight, FileText } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import apiClient from "../helpers/apiClient";

const Header = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [activeDropdown, setActiveDropdown] = useState(null);
	const dropdownRef = useRef(null);
	const [portfolioItems, setPortfolioItems] = useState([]);
	const [newsItems, setNewsItems] = useState([]);
	const [isScrolling, setIsScrolling] = useState(false);

	// Organized services by category for mega menu
	const servicesCategories = [
		{
			title: "Development",
			services: [
				{
					icon: <Globe className="w-5 h-5" />,
					title: "Web Development",
					description: "Custom React & Node.js applications",
					href: "/services/web-development"
				},
				{
					icon: <Smartphone className="w-5 h-5" />,
					title: "Mobile Apps",
					description: "React Native & native development",
					href: "/services/mobile-development"
				},
				{
					icon: <Code className="w-5 h-5" />,
					title: "WordPress Development",
					description: "Custom themes & plugin development",
					href: "/services/wordpress-development"
				}
			]
		},
		{
			title: "Infrastructure & Strategy",
			services: [
				{
					icon: <Database className="w-5 h-5" />,
					title: "System Architecture",
					description: "Backend & database solutions",
					href: "/services/system-architecture"
				},
				{
					icon: <Shield className="w-5 h-5" />,
					title: "Digital Transformation",
					description: "Legacy system modernisation",
					href: "/services/digital-transformation"
				}
			]
		},
		{
			title: "Compliance & Support",
			services: [
				{
					icon: <Shield className="w-5 h-5" />,
					title: "EAA Compliance",
					description: "European Accessibility Act compliance",
					href: "/services/eaa-compliance",
					isUrgent: true
				},
				{
					icon: <Settings className="w-5 h-5" />,
					title: "Maintenance & Support",
					description: "Ongoing support & optimisation",
					href: "/services/maintenance-support"
				}
			]
		}
	];

	// Flatten services for mobile menu (keeping original structure)
	const services = servicesCategories.flatMap(category => category.services);

	// Create portfolio dropdown items with "View All" option
	const portfolioDropdownItems = [
		...portfolioItems,
		{
			title: "View All Projects",
			description: "See our complete portfolio",
			href: "/portfolio",
			isViewAll: true
		}
	];

	// Create news dropdown items with "View All" option
	const newsDropdownItems = [
		...newsItems,
		{
			title: "View All Articles",
			description: "Read our latest insights",
			href: "/news",
			isViewAll: true
		}
	];

	const navigation = [
		{ name: 'Home', href: '/' },
		{
			name: 'Services',
			href: '/services',
			hasDropdown: true,
			hasMegaMenu: true,
			dropdownItems: services // For mobile
		},
		{ name: 'About', href: '/about' },
		{
			name: 'Portfolio',
			href: '/portfolio',
			hasDropdown: true,
			dropdownItems: portfolioDropdownItems
		},
		{
			name: 'News',
			href: '/news',
			hasDropdown: true,
			dropdownItems: newsDropdownItems
		},
		{ name: 'Contact', href: '/contact' }
	];

	useEffect(() => {
		getPortfolio();
		getNews();
	}, []);

	const getPortfolio = async () => {
		try {
			// Fetch featured news with limit
			const response = await apiClient.getPortfolio(true, 3);

			if (response.success) {
				setPortfolioItems(response.data.sort((a, b) => b.id - a.id) || []);
			} else {
				console.error('Error fetching news:', response.error);
			}
		} catch (e) {
			console.error('Error getting news items: ', e);
		}
	};

	const getNews = async () => {
		try {
			// Fetch featured news with limit
			const response = await apiClient.getFeaturedNews(3);

			if (response.success) {
				setNewsItems(response.data.sort((a, b) => b.id - a.id) || []);
			} else {
				console.error('Error fetching news:', response.error);
			}
		} catch (e) {
			console.error('Error getting news items: ', e);
		}
	};

	const isActive = (path) => {
		if (path === '/' && location.pathname === '/') return true;
		if (path !== '/' && location.pathname.startsWith(path)) return true;
		return false;
	};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			// Only handle click outside for desktop dropdowns
			if (window.innerWidth >= 768) { // md breakpoint
				if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
					setActiveDropdown(null);
				}
			}

			// Handle mobile menu close when clicking outside
			if (isMenuOpen && !event.target.closest('.mobile-menu-container')) {
				setIsMenuOpen(false);
				setActiveDropdown(null); // Also close any open dropdowns
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isMenuOpen, activeDropdown]);

	// Close menu on route change
	useEffect(() => {
		setIsMenuOpen(false);
		setActiveDropdown(null);
	}, [location.pathname]);

	// Prevent body scroll when menu is open
	useEffect(() => {
		if (isMenuOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'unset';
		}
		return () => {
			document.body.style.overflow = 'unset';
		};
	}, [isMenuOpen]);

	// Add this helper function to handle touch events
	const handleTouchStart = (e) => {
		setIsScrolling(false);
	};

	const handleTouchMove = (e) => {
		setIsScrolling(true);
	};

	const handleTouchEnd = (e, href) => {
		e.preventDefault();
		e.stopPropagation();

		// Only navigate if user wasn't scrolling
		if (!isScrolling) {
			handleMobileSubmenuClick(href, e);
		}

		// Reset scrolling state after a short delay
		setTimeout(() => setIsScrolling(false), 100);
	};

	const handleDropdownToggle = (itemName, e) => {
		e.preventDefault();
		e.stopPropagation();

		// Toggle the dropdown
		const newActiveDropdown = activeDropdown === itemName ? null : itemName;
		setActiveDropdown(newActiveDropdown);
	};

	// Helper function to close all mobile menu states
	const closeMobileMenu = () => {
		setIsMenuOpen(false);
		setActiveDropdown(null);
	};

	// Handle mobile submenu link clicks with debugging
	const handleMobileSubmenuClick = (href, e) => {
		e.stopPropagation();
		e.preventDefault();

		// Close menu states immediately
		setIsMenuOpen(false);
		setActiveDropdown(null);

		// Use React Router navigate
		setTimeout(() => {
			navigate(href);
		}, 100);
	};

	return (
		<>
			<nav className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100">
				<div className="max-w-6xl mx-auto px-6">
					<div className="flex justify-between items-center h-20">
						<div className="flex items-center space-x-3">
							<Link to="/" className="flex items-center space-x-3">
								<div className="w-10 h-10 bg-gradient-to-br from-slate-900 to-slate-700 rounded-xl flex items-center justify-center">
									<span className="text-white font-bold text-lg">K</span>
								</div>
								<div>
									<div className="text-xl font-bold text-slate-900 tracking-tight">KGC Innovations</div>
									<div className="text-xs text-slate-500 font-medium -mt-1">Limited</div>
								</div>
							</Link>
						</div>

						{/* Desktop Navigation */}
						<div className="hidden md:flex items-center space-x-8" ref={dropdownRef}>
							{navigation.slice(1, -1).map((item) => (
								<div key={item.name} className="relative">
									{item.hasDropdown ? (
										<>
											<button
												onClick={() => handleDropdownToggle(item.name)}
												onMouseEnter={() => setActiveDropdown(item.name)}
												className={`flex items-center text-slate-600 hover:text-slate-900 font-medium transition-colors ${isActive(item.href) ? 'text-slate-900' : ''
													}`}
											>
												{item.name}
												<ChevronDown className={`ml-1 w-4 h-4 transition-transform ${activeDropdown === item.name ? 'rotate-180' : ''
													}`} />
											</button>

											{/* Dropdown/Mega Menu */}
											{activeDropdown === item.name && (
												<div
													className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-200 py-6 z-50 ${item.hasMegaMenu ? 'w-[720px]' : 'w-80'
														}`}
													onMouseLeave={() => setActiveDropdown(null)}
												>
													{item.name === 'Services' && item.hasMegaMenu ? (
														// Mega Menu for Services
														<div className="px-8">
															<div className="text-sm font-medium text-slate-500 mb-6 uppercase tracking-wide">
																Our Services
															</div>
															<div className="grid grid-cols-3 gap-8">
																{servicesCategories.map((category, categoryIndex) => (
																	<div key={categoryIndex} className="space-y-4">
																		<h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-2">
																			{category.title}
																		</h3>
																		<div className="space-y-3">
																			{category.services.map((service, serviceIndex) => (
																				<Link
																					key={serviceIndex}
																					to={service.href}
																					className="flex items-start p-3 rounded-lg hover:bg-slate-50 transition-colors group"
																				>
																					<div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-slate-200 transition-colors flex-shrink-0">
																						{service.icon}
																					</div>
																					<div className="flex-1 min-w-0">
																						<div className="font-medium text-slate-900 mb-1 flex items-center">
																							{service.title}
																							{service.isUrgent && (
																								<span className="ml-2 px-2 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-full">
																									URGENT
																								</span>
																							)}
																						</div>
																						<div className="text-xs text-slate-600 line-clamp-2">
																							{service.description}
																						</div>
																					</div>
																				</Link>
																			))}
																		</div>
																	</div>
																))}
															</div>
														</div>
													) : (
														// Regular dropdown for Portfolio and News
														<div className="px-6">
															<div className="text-sm font-medium text-slate-500 mb-4 uppercase tracking-wide">
																{item.name === 'Portfolio' ? 'Recent Projects' : 'Latest Articles'}
															</div>
															<div className="space-y-3">
																{item.dropdownItems.map((dropdownItem, index) => {
																	const href = item.name === 'Services' ? dropdownItem.href :
																		item.name === 'Portfolio' ?
																			(dropdownItem.isViewAll ? dropdownItem.href : `/portfolio/${dropdownItem.slug}`) :
																			item.name === 'News' ?
																				(dropdownItem.isViewAll ? dropdownItem.href : `/news/${dropdownItem.slug}`) :
																				dropdownItem.href;

																	return (
																		<Link
																			key={index}
																			to={href}
																			className={`block p-3 rounded-lg hover:bg-slate-50 transition-colors group ${dropdownItem.isViewAll ? 'border-t border-slate-200 mt-3 pt-4' : ''
																				}`}
																		>
																			<div className="flex items-center justify-between">
																				<div>
																					<div className={`font-medium mb-1 ${dropdownItem.isViewAll ? 'text-slate-900' : 'text-slate-900'
																						}`}>
																						{dropdownItem.title}
																					</div>
																					<div className="text-sm text-slate-600">
																						{dropdownItem.description}
																					</div>
																				</div>
																				<ArrowRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
																			</div>
																		</Link>
																	);
																})}
															</div>
														</div>
													)}
												</div>
											)}
										</>
									) : (
										<Link
											to={item.href}
											className={`text-slate-600 hover:text-slate-900 font-medium transition-colors ${isActive(item.href) ? 'text-slate-900' : ''
												}`}
										>
											{item.name}
										</Link>
									)}
								</div>
							))}
							<Link
								to="/contact"
								className="bg-slate-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-colors"
							>
								Get Started
							</Link>
						</div>

						{/* Mobile menu button */}
						<button
							onClick={() => setIsMenuOpen(!isMenuOpen)}
							className="md:hidden p-2 text-slate-600 hover:text-slate-900 transition-colors relative z-50"
						>
							{isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
						</button>
					</div>
				</div>
			</nav>

			{/* Mobile Menu Overlay */}
			<div className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
				}`}>
				{/* Background overlay with shadow */}
				<div
					className="absolute inset-0 bg-black/30 backdrop-blur-sm"
					onClick={() => setIsMenuOpen(false)}
				/>

				{/* Sliding sidebar */}
				<div className="mobile-menu-container">
					<div className={`absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out overflow-y-auto ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'
						}`}>
						{/* Menu header */}
						<div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white">
							<div className="flex items-center space-x-3">
								<div className="w-8 h-8 bg-gradient-to-br from-slate-900 to-slate-700 rounded-lg flex items-center justify-center">
									<span className="text-white font-bold text-sm">K</span>
								</div>
								<div>
									<div className="text-lg font-bold text-slate-900 tracking-tight">KGC Innovations</div>
									<div className="text-xs text-slate-500 font-medium -mt-1">Limited</div>
								</div>
							</div>
							<button
								onClick={() => setIsMenuOpen(false)}
								className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
							>
								<X className="w-5 h-5" />
							</button>
						</div>

						{/* Menu content */}
						<div className="px-6 py-8">
							<div className="space-y-6">
								{navigation.slice(0, -1).map((item) => (
									<div key={item.name}>
										{item.hasDropdown ? (
											<>
												<button
													onClick={(e) => handleDropdownToggle(item.name, e)}
													className={`flex items-center justify-between w-full py-3 text-lg font-medium transition-colors ${isActive(item.href)
														? 'text-slate-900 border-l-4 border-slate-900 pl-4 bg-slate-50 -ml-4 rounded-r-lg'
														: 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 -mx-4 px-4 rounded-lg'
														}`}
													style={{
														WebkitTapHighlightColor: 'transparent',
														touchAction: 'manipulation'
													}}
												>
													<span className="flex items-center">
														{item.name === 'News' && <FileText className="w-5 h-5 mr-2" />}
														{item.name}
													</span>
													<ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === item.name ? 'rotate-180' : ''
														}`} />
												</button>

												{/* Mobile Dropdown */}
												{activeDropdown === item.name && (
													<div className="mt-3 ml-4 space-y-2">
														{item.dropdownItems.map((dropdownItem, index) => {
															const href = item.name === 'Services' ? dropdownItem.href :
																item.name === 'Portfolio' ?
																	(dropdownItem.isViewAll ? dropdownItem.href : `/portfolio/${dropdownItem.slug}`) :
																	item.name === 'News' ?
																		(dropdownItem.isViewAll ? dropdownItem.href : `/news/${dropdownItem.slug}`) :
																		dropdownItem.href;

															return (
																<button
																	key={index}
																	onClick={(e) => handleMobileSubmenuClick(href, e)}
																	onTouchStart={handleTouchStart}
																	onTouchMove={handleTouchMove}
																	onTouchEnd={(e) => handleTouchEnd(e, href)}
																	className={`w-full text-left flex items-start p-3 rounded-lg hover:bg-slate-50 transition-colors group cursor-pointer border-0 bg-transparent ${dropdownItem.isViewAll ? 'border-t border-slate-200 mt-3 pt-4' : ''
																		}`}
																	style={{
																		WebkitTapHighlightColor: 'transparent',
																		touchAction: 'manipulation',
																		zIndex: 10,
																		position: 'relative'
																	}}
																>
																	{item.name === 'Services' && (
																		<div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-slate-200 transition-colors pointer-events-none">
																			{dropdownItem.icon}
																		</div>
																	)}
																	<div className="flex-1 pointer-events-none">
																		<div className="font-medium text-slate-800 text-sm mb-1 flex items-center">
																			{dropdownItem.title}
																			{dropdownItem.isUrgent && (
																				<span className="ml-2 px-2 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-full">
																					URGENT
																				</span>
																			)}
																		</div>
																		<div className="text-xs text-slate-600">
																			{dropdownItem.description}
																		</div>
																	</div>
																	<ArrowRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
																</button>
															);
														})}
													</div>
												)}
											</>
										) : (
											<Link
												to={item.href}
												onClick={closeMobileMenu}
												className={`block py-3 text-lg font-medium transition-colors ${isActive(item.href)
													? 'text-slate-900 border-l-4 border-slate-900 pl-4 bg-slate-50 -ml-4 rounded-r-lg'
													: 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 -mx-4 px-4 rounded-lg'
													}`}
											>
												{item.name}
											</Link>
										)}
									</div>
								))}
							</div>

							{/* CTA button */}
							<div className="mt-12 pt-8 border-t border-gray-100">
								<Link
									to="/contact"
									onClick={closeMobileMenu}
									className="block w-full bg-slate-900 text-white text-center px-6 py-4 rounded-lg font-medium hover:bg-slate-800 transition-colors"
								>
									Get Started
								</Link>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default Header;